import { isAuthenticatedAtom } from '@app/state/authAtom'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { createContext, useEffect, useCallback, useRef, useMemo } from 'react'
import { useAuth } from '@app/Auth/hooks/useAuth'
import { useLocation, useNavigate } from 'react-router-dom'
import { isLoadingAuthStateAtom } from '@app/state/authAtom'
import { toast } from '@app/components/ui/Toast/toast'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'
import { sidebarFooterLoadedAtom } from '@app/state/sidebarAtom'
import { subdomainAtom } from '@app/state/subdomainAtom'

export const AuthStateContext = createContext({})

const generateSupportEmail = (error: string) => {
  const supportEmail = 'support@urbanintelligence.co.nz'
  const subject = `Support Request - ${error}`
  const body = `Dear Support Team,\n\nI encountered the following error: ${error}\n\nPlease assist me in resolving this issue.\n\nThank you.`

  return `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

export const AuthStateProvider = ({ children }: { children: React.ReactNode }) => {
  const postLoginRef = useRef(false)
  const { search } = useLocation()
  const { fetchPostLoginUserProfile, initiateLogin } = useAuth()
  const isSubdomain = useAtomValue(subdomainAtom)

  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const setSidebarMenuLoaded = useSetAtom(sidebarFooterLoadedAtom)
  const setSidebarFooterLoaded = useSetAtom(sidebarFooterLoadedAtom)
  const [isLoadingAuth, setIsLoadingAuth] = useAtom(isLoadingAuthStateAtom)

  useEffect(() => {
    if (!isAuthenticated) {
      setSidebarMenuLoaded(true)
      setSidebarFooterLoaded(true)
    }
  }, [isAuthenticated, setSidebarMenuLoaded, setSidebarFooterLoaded])

  useEffect(() => {
    if (isSubdomain && window.location.pathname === '/') {
      initiateLogin()
    }
  }, [isSubdomain, initiateLogin])

  const errorMap = useMemo(() => {
    return {
      login_failed: (
        <p>
          Login Failed - Please try again later and contact{' '}
          <a
            style={{ color: 'inherit', textDecoration: 'underline' }}
            href={generateSupportEmail('Login Failed')}
          >
            support
          </a>{' '}
          if this continues.
        </p>
      ),
      invite_failed: (
        <p>
          Invitation Failed - This has been logged, please contact{' '}
          <a
            style={{ color: 'inherit', textDecoration: 'underline' }}
            href={generateSupportEmail('Invitation Failed')}
          >
            support
          </a>{' '}
          if this continues.
        </p>
      ),
      invite_expired_resent: (
        <p>Invitation Expired - We resent the invitation, please check your email.</p>
      ),
      confirmation_failed: (
        <p>
          Signup Confirmation Failed - This has been logged, please contact{' '}
          <a
            style={{ color: 'inherit', textDecoration: 'underline' }}
            href={generateSupportEmail('Signup Confirmation Failed')}
          >
            support
          </a>{' '}
          if this continues.
        </p>
      ),
      confirmation_code_expired_resent: (
        <p>
          Signup Confirmation Code Expired - We resent the confirmation code, please check your
          email.
        </p>
      ),
    }
  }, [])

  const callFetchPostLoginUserProfile = useCallback(() => {
    fetchPostLoginUserProfile()
  }, [fetchPostLoginUserProfile])

  useEffect(() => {
    if (search.includes('login=success') && !isAuthenticated && !postLoginRef.current) {
      setIsLoadingAuth(true)
      postLoginRef.current = true
      callFetchPostLoginUserProfile()
    }
    if (search.includes('error=true')) {
      const params = new URLSearchParams(search)
      const errorType = params.get('message')
      const errorMessage = errorMap[errorType as keyof typeof errorMap] || 'Unknown Error'

      toast({
        description: '',
        type: 'error',
        position: 'top-center',
        duration: 10000,
        children: <p>{errorMessage}</p>,
      })
    }
  }, [search, isAuthenticated, callFetchPostLoginUserProfile, setIsLoadingAuth, errorMap])

  return (
    <AuthStateContext.Provider value={{}}>
      {children}
      {isLoadingAuth && <LoadingSpinner />}
    </AuthStateContext.Provider>
  )
}
